import { useState } from 'react';
import { X, ChevronDown, ChevronUp, Lightbulb, BookOpen, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface HelpSection {
  title: string;
  content: string;
  steps?: string[];
}

export interface PageHelpContent {
  title: string;
  summary: string;
  sections: HelpSection[];
  relatedPages?: { name: string; path: string }[];
  tips?: string[];
}

interface PageHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: PageHelpContent | null;
}

export function PageHelpModal({ isOpen, onClose, content }: PageHelpModalProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  if (!isOpen || !content) return null;

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <BookOpen className="w-6 h-6" />
                <h2 className="text-xl font-semibold">{content.title}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-lg transition"
                aria-label="Close help"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
            {/* Summary */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
              <p className="text-gray-700 leading-relaxed">{content.summary}</p>
            </div>

            {/* Detailed Sections */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Detailed Instructions
              </h3>
              {content.sections.map((section, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition"
                  >
                    <span className="font-medium text-gray-900">{section.title}</span>
                    {expandedSections.has(index) ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections.has(index) && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <p className="text-gray-600 mt-3 mb-3">{section.content}</p>
                      
                      {section.steps && section.steps.length > 0 && (
                        <ol className="list-decimal list-inside space-y-2 text-gray-600 bg-gray-50 rounded-lg p-4">
                          {section.steps.map((step, stepIndex) => (
                            <li key={stepIndex} className="leading-relaxed">
                              {step}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tips */}
            {content.tips && content.tips.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                  <Lightbulb className="w-4 h-4 mr-2 text-yellow-500" />
                  Tips
                </h3>
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                  <ul className="space-y-2">
                    {content.tips.map((tip, index) => (
                      <li key={index} className="flex items-start text-gray-700">
                        <span className="text-yellow-500 mr-2">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Related Pages */}
            {content.relatedPages && content.relatedPages.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Related Pages
                </h3>
                <div className="flex flex-wrap gap-2">
                  {content.relatedPages.map((page, index) => (
                    <Link
                      key={index}
                      to={page.path}
                      onClick={onClose}
                      className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition"
                    >
                      {page.name}
                      <ExternalLink className="w-3 h-3 ml-1.5" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Deep-links to System Manual & Architecture Flowcharts (D-IA-04 / D-IA-10) */}
            <div className="pt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
              <span>Need architecture diagrams, data flows, or full SOP manuals?</span>
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/system-manual"
                  onClick={onClose}
                  className="inline-flex items-center px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded-lg transition"
                >
                  System Manual & Flowcharts
                  <ExternalLink className="w-3 h-3 ml-1.5" />
                </Link>
                <Link
                  to="/manuals"
                  onClick={onClose}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-lg transition"
                >
                  Staff Manuals
                  <ExternalLink className="w-3 h-3 ml-1.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
